"use client";
import { useEffect } from "react";
import { BASE_URL, REGISTER_URL, BOOK_DEMO_URL, HELP_URL, ROUTES, STATS } from "@/lib/constants";

interface ModelContextTool {
  name: string;
  title?: string;
  description: string;
  inputSchema?: Record<string, unknown>;
  execute: (input: Record<string, unknown>) => Promise<unknown>;
  annotations?: { readOnlyHint?: boolean };
}

interface ModelContext {
  registerTool: (tool: ModelContextTool, options?: { signal?: AbortSignal }) => void;
}

export function WebMcpTools() {
  useEffect(() => {
    const mc = (document as unknown as { modelContext?: ModelContext }).modelContext;
    if (!mc) return;

    const controller = new AbortController();
    const { signal } = controller;

    mc.registerTool(
      {
        name: "get_proxy_product_info",
        title: "Get Proxy Product Info",
        description:
          "Returns details about a Proxidize proxy product type including description, features, and links.",
        inputSchema: {
          type: "object",
          properties: {
            product: {
              type: "string",
              enum: ["mobile", "residential"],
              description: "The proxy product type to retrieve information about.",
            },
          },
          required: ["product"],
        },
        execute: async (input) => {
          if (input.product === "mobile") {
            return {
              name: "Mobile Proxies",
              description: "Real 4G/5G mobile IPs with the highest trust scores of any proxy type.",
              useCases: [
                "Ad verification",
                "Social media monitoring",
                "Market research",
                "Web scraping",
              ],
              productUrl: `${BASE_URL}${ROUTES.productMobileProxies}`,
              pricingUrl: `${BASE_URL}${ROUTES.pricingMobileProxies}`,
            };
          }
          return {
            name: "Residential Proxies",
            description:
              "Ethically sourced residential IPs from real devices. Pay-as-you-go GB pricing.",
            useCases: ["Price monitoring", "SEO tracking", "Data collection", "Brand protection"],
            productUrl: `${BASE_URL}${ROUTES.productResidentialProxies}`,
            pricingUrl: `${BASE_URL}${ROUTES.pricingResidentialProxies}`,
          };
        },
        annotations: { readOnlyHint: true },
      },
      { signal }
    );

    mc.registerTool(
      {
        name: "get_company_stats",
        title: "Get Proxidize Stats",
        description:
          "Returns Proxidize's key statistics: available IPs, country coverage, uptime SLA, and customer count.",
        inputSchema: { type: "object", properties: {} },
        execute: async () => ({
          ips: STATS.ips,
          countries: STATS.countries,
          uptime: STATS.uptime,
          customers: STATS.customers,
          helpCenter: HELP_URL,
        }),
        annotations: { readOnlyHint: true },
      },
      { signal }
    );

    mc.registerTool(
      {
        name: "navigate_to_page",
        title: "Navigate to Page",
        description: "Navigates the browser to a specific page on the Proxidize website.",
        inputSchema: {
          type: "object",
          properties: {
            page: {
              type: "string",
              enum: [
                "home",
                "mobile-proxies",
                "residential-proxies",
                "pricing-mobile",
                "pricing-residential",
                "blog",
                "signup",
                "book-demo",
              ],
              description: "The destination page.",
            },
          },
          required: ["page"],
        },
        execute: async (input) => {
          const pages: Record<string, string> = {
            home: BASE_URL,
            "mobile-proxies": `${BASE_URL}${ROUTES.productMobileProxies}`,
            "residential-proxies": `${BASE_URL}${ROUTES.productResidentialProxies}`,
            "pricing-mobile": `${BASE_URL}${ROUTES.pricingMobileProxies}`,
            "pricing-residential": `${BASE_URL}${ROUTES.pricingResidentialProxies}`,
            blog: `${BASE_URL}${ROUTES.blog}`,
            signup: REGISTER_URL,
            "book-demo": BOOK_DEMO_URL,
          };
          const url = pages[input.page as string];
          if (url) window.location.href = url;
          return { navigated: !!url, url: url ?? null };
        },
      },
      { signal }
    );

    return () => controller.abort();
  }, []);

  return null;
}
